/**
 * Le matériel : où est chaque outil, chaque machine.
 *
 * Décidé avec le patron le 30 octobre 2026 (migration `20261030120000`) :
 *
 *   · les machines et l'outillage qui ont un prix se suivent **à l'unité** ;
 *     le petit outillage (étais, serre-joints…) **en quantités** ;
 *   · trois lieux — dépôt, chantier, réparation — et le matériel **loué**
 *     porte son loueur et sa date de retour ;
 *   · le bureau déplace partout, le chef de chantier sur ses chantiers,
 *     l'ouvrier voit ; **le patron seul** fait entrer ou sortir du parc ;
 *   · chaque mouvement s'inscrit dans l'historique.
 *
 * Le type est un texte libre : `EQUIPMENT_CATALOG` propose les plus courants
 * du marché, et ce que le patron saisit à la main vaut autant. Seule la
 * catégorie est contrainte, en base comme ici — c'est elle qui range l'écran.
 */
import type { Role } from "../../context.js";

export const EQUIPMENT_CATEGORIES = [
  "terrassement",
  "levage",
  "electroportatif",
  "decoupe",
  "mesure",
  "energie",
  "nettoyage",
  "finition",
  "fluides",
  "securite",
  "autre",
] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

export const EQUIPMENT_CATEGORY_LABELS: Record<EquipmentCategory, string> = {
  terrassement: "Terrassement et gros œuvre",
  levage: "Levage, accès, échafaudage",
  electroportatif: "Électroportatif",
  decoupe: "Découpe et sciage",
  mesure: "Mesure et traçage",
  energie: "Énergie, air, chauffage",
  nettoyage: "Nettoyage",
  finition: "Peinture, enduit, finition",
  fluides: "Plomberie, électricité, soudure",
  securite: "Sécurité et balisage",
  autre: "Autre",
};

/**
 * Les machines et outils les plus courants sur un chantier du bâtiment, rangés
 * par catégorie. Une proposition, pas une liste fermée : « Autre… » ouvre la
 * saisie libre.
 */
export const EQUIPMENT_CATALOG: Readonly<Record<EquipmentCategory, readonly string[]>> = {
  terrassement: [
    "Mini-pelle",
    "Pelle sur pneus",
    "Mini-chargeuse",
    "Chargeuse",
    "Tractopelle",
    "Dumper",
    "Plaque vibrante",
    "Pilonneuse",
    "Rouleau compacteur",
    "Bétonnière",
    "Malaxeur",
    "Aiguille vibrante",
    "Brise-roche",
  ],
  levage: [
    "Échafaudage roulant",
    "Échafaudage de façade",
    "Nacelle ciseaux",
    "Nacelle articulée",
    "Chariot télescopique",
    "Monte-matériaux",
    "Treuil de levage",
    "Lève-plaque",
    "Échelle",
    "Escabeau",
    "Plateforme individuelle roulante",
  ],
  electroportatif: [
    "Perforateur",
    "Burineur",
    "Perceuse-visseuse",
    "Visseuse à choc",
    "Visseuse plaquiste",
    "Meuleuse",
    "Scie sauteuse",
    "Scie sabre",
    "Rabot électrique",
    "Ponceuse",
    "Défonceuse",
    "Décapeur thermique",
    "Cloueur",
    "Agrafeuse",
    "Malaxeur électrique",
  ],
  decoupe: [
    "Scie circulaire",
    "Scie plongeante",
    "Scie à onglet",
    "Scie sur table",
    "Scie à carrelage",
    "Découpeuse thermique",
    "Scie de sol",
    "Carotteuse",
    "Rainureuse",
  ],
  mesure: [
    "Laser rotatif",
    "Niveau laser croix",
    "Niveau de chantier",
    "Télémètre laser",
    "Détecteur de réseaux",
    "Humidimètre",
    "Caméra thermique",
    "Théodolite",
  ],
  energie: [
    "Groupe électrogène",
    "Compresseur",
    "Tour d’éclairage",
    "Projecteur de chantier",
    "Chauffage de chantier",
    "Déshumidificateur",
    "Armoire électrique de chantier",
  ],
  nettoyage: ["Aspirateur de chantier", "Nettoyeur haute pression", "Autolaveuse", "Souffleur"],
  finition: [
    "Pistolet à peinture airless",
    "Machine à projeter l’enduit",
    "Ponceuse girafe",
    "Ponceuse à parquet",
    "Décolleuse à papier peint",
    "Malaxeur à mortier",
  ],
  fluides: [
    "Sertisseuse",
    "Cintreuse",
    "Poste à souder",
    "Chalumeau",
    "Pompe de relevage",
    "Déboucheur",
    "Caméra d’inspection",
  ],
  securite: ["Barrières de chantier", "Clôture mobile", "Ligne de vie", "Signalisation temporaire"],
  autre: [],
};

/**
 * Le petit outillage le plus courant, suivi en quantités. L'unité suit le
 * type : on compte des étais, on mesure des rallonges en nombre, pas en mètres.
 */
export const BULK_CATALOG: readonly { label: string; category: EquipmentCategory; unit: string }[] = [
  { label: "Étai", category: "securite", unit: "unité" },
  { label: "Serre-joint", category: "autre", unit: "unité" },
  { label: "Tréteau", category: "autre", unit: "unité" },
  { label: "Barrière de chantier", category: "securite", unit: "unité" },
  { label: "Plot de signalisation", category: "securite", unit: "unité" },
  { label: "Élingue", category: "levage", unit: "unité" },
  { label: "Sangle", category: "levage", unit: "unité" },
  { label: "Plateau d’échafaudage", category: "levage", unit: "unité" },
  { label: "Rallonge électrique", category: "energie", unit: "unité" },
  { label: "Enrouleur", category: "energie", unit: "unité" },
  { label: "Bac à gravats", category: "terrassement", unit: "unité" },
  { label: "Brouette", category: "terrassement", unit: "unité" },
  { label: "Auge", category: "terrassement", unit: "unité" },
  { label: "Seau", category: "finition", unit: "unité" },
  { label: "Règle de maçon", category: "mesure", unit: "unité" },
  { label: "Cale", category: "autre", unit: "unité" },
];

export type EquipmentLocationKind = "depot" | "chantier" | "reparation";
export const EQUIPMENT_LOCATION_KINDS: readonly EquipmentLocationKind[] = ["depot", "chantier", "reparation"];
export const EQUIPMENT_LOCATION_LABELS: Record<EquipmentLocationKind, string> = {
  depot: "Au dépôt",
  chantier: "Sur chantier",
  reparation: "En réparation",
};

export type EquipmentOwnership = "owned" | "rented";

export const EQUIPMENT_ARCHIVE_REASONS = ["rendu_loueur", "vendu", "hors_service", "perdu", "autre"] as const;
export type EquipmentArchiveReason = (typeof EQUIPMENT_ARCHIVE_REASONS)[number];
export const EQUIPMENT_ARCHIVE_REASON_LABELS: Record<EquipmentArchiveReason, string> = {
  rendu_loueur: "Rendu au loueur",
  vendu: "Vendu",
  hors_service: "Hors service",
  perdu: "Perdu ou volé",
  autre: "Autre raison",
};

/** Une machine ou un outil suivi à l'unité. */
export interface Equipment {
  id: string;
  name: string;
  typeLabel: string;
  category: EquipmentCategory;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  ownership: EquipmentOwnership;
  rentalCompany: string | null;
  rentalReturnOn: string | null;
  locationKind: EquipmentLocationKind;
  projectId: string | null;
  /** Nul quand l'appelant ne lit pas `projects` — le terrain le sait par sa mission. */
  projectName: string | null;
  locatedSince: string;
  notes: string | null;
  /** `<entreprise>/<machine>/<id>.<ext>` dans `equipment-photos`. */
  photoPath: string | null;
  archivedAt: string | null;
  archiveReason: EquipmentArchiveReason | null;
}

export interface BulkPlacement {
  projectId: string;
  projectName: string | null;
  quantity: number;
  since: string;
}

/** Un petit outillage suivi en quantités. */
export interface BulkItem {
  id: string;
  name: string;
  typeLabel: string;
  category: EquipmentCategory;
  unit: string;
  /** Un lot loué se saisit à part : « 40 étais loués chez Kiloutou ». */
  ownership: EquipmentOwnership;
  rentalCompany: string | null;
  rentalReturnOn: string | null;
  totalQuantity: number;
  atDepot: number;
  /** Ce qui est en réparation — le reste du lot continue de tourner. */
  atRepair: number;
  notes: string | null;
  archivedAt: string | null;
  archiveReason: EquipmentArchiveReason | null;
  /** Les chantiers que l'appelant voit — la base filtre. */
  placements: BulkPlacement[];
}

export interface EquipmentMovement {
  id: string;
  equipmentId: string | null;
  bulkItemId: string | null;
  quantity: number | null;
  fromKind: EquipmentLocationKind | null;
  fromProjectId: string | null;
  fromProjectName: string | null;
  toKind: EquipmentLocationKind | null;
  toProjectId: string | null;
  toProjectName: string | null;
  movedBy: string | null;
  movedAt: string;
  note: string | null;
}

export interface EquipmentFleet {
  units: Equipment[];
  bulk: BulkItem[];
}

// ─── Les droits, dits ici pour l'écran — la base les tient de son côté ────

/** Faire entrer ou sortir du parc, modifier une fiche : le patron seul. */
export function canManageFleet(role: Role | null | undefined): boolean {
  return role === "patron";
}

/** Déplacer : le bureau partout, le chef de chantier sur ses chantiers. */
export function canMoveEquipment(role: Role | null | undefined): boolean {
  return role === "patron" || role === "conducteur" || role === "assistant" || role === "chef_de_chantier";
}

/** Relire l'historique : le bureau. */
export function canReadEquipmentHistory(role: Role | null | undefined): boolean {
  return role === "patron" || role === "conducteur" || role === "assistant" || role === "lecture_seule";
}

// ─── Lecture ──────────────────────────────────────────────────────────────

/** Où est-il, dit comme on le dirait : « Au dépôt », « Chantier Martin », « En réparation ». */
export function placeLabel(kind: EquipmentLocationKind | null, projectName?: string | null): string {
  if (kind === null) return "Hors du parc";
  if (kind === "chantier") return projectName ? `Chantier ${projectName}` : "Sur un chantier";
  return EQUIPMENT_LOCATION_LABELS[kind];
}

export type RentalState = "none" | "ok" | "due_soon" | "overdue";

/**
 * Une location rendue en retard se paie au jour. « Bientôt » = dans les deux
 * jours : le temps de prévenir le loueur et de charger le camion.
 */
export function rentalState(
  item: Pick<Equipment | BulkItem, "ownership" | "rentalReturnOn">,
  today: string,
): RentalState {
  if (item.ownership !== "rented" || !item.rentalReturnOn) return "none";
  const days = Math.round(
    (Date.parse(`${item.rentalReturnOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  if (days < 0) return "overdue";
  if (days <= 2) return "due_soon";
  return "ok";
}

export interface EquipmentPlaceGroup {
  key: string;
  kind: EquipmentLocationKind;
  projectId: string | null;
  label: string;
  units: Equipment[];
}

const byTypeThenName = (a: Equipment, b: Equipment) =>
  a.typeLabel.localeCompare(b.typeLabel, "fr") || a.name.localeCompare(b.name, "fr");

/**
 * Le parc rangé par lieu : le dépôt d'abord, puis les chantiers par nom, la
 * réparation à la fin. Dans chaque lieu, par type puis par nom. Les machines
 * sorties du parc n'y figurent pas.
 */
export function groupByPlace(units: readonly Equipment[]): EquipmentPlaceGroup[] {
  const groups = new Map<string, EquipmentPlaceGroup>();
  for (const u of units) {
    if (u.archivedAt) continue;
    const key = u.locationKind === "chantier" ? `chantier:${u.projectId}` : u.locationKind;
    let g = groups.get(key);
    if (!g) {
      g = { key, kind: u.locationKind, projectId: u.projectId, label: placeLabel(u.locationKind, u.projectName), units: [] };
      groups.set(key, g);
    }
    g.units.push(u);
  }
  const rank = (g: EquipmentPlaceGroup) => (g.kind === "depot" ? 0 : g.kind === "chantier" ? 1 : 2);
  return [...groups.values()]
    .map((g) => ({ ...g, units: [...g.units].sort(byTypeThenName) }))
    .sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label, "fr"));
}

/** Ce qui est sur un chantier : les machines, et le petit outillage avec sa quantité. */
export function onSite(
  fleet: EquipmentFleet,
  projectId: string,
): { units: Equipment[]; bulk: { item: BulkItem; quantity: number }[] } {
  return {
    units: fleet.units
      .filter((u) => !u.archivedAt && u.locationKind === "chantier" && u.projectId === projectId)
      .sort(byTypeThenName),
    bulk: fleet.bulk
      .filter((b) => !b.archivedAt)
      .flatMap((item) => {
        const p = item.placements.find((pl) => pl.projectId === projectId);
        return p ? [{ item, quantity: p.quantity }] : [];
      })
      .sort((a, b) => a.item.name.localeCompare(b.item.name, "fr")),
  };
}

/** Recherche insensible aux accents et à la casse : « hilti », « perfo », un numéro de série. */
export function matchesEquipment(
  item: Pick<Equipment, "name" | "typeLabel"> & Partial<Pick<Equipment, "brand" | "model" | "serialNumber">>,
  query: string,
): boolean {
  const norm = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
  const q = norm(query.trim());
  if (!q) return true;
  return [item.name, item.typeLabel, item.brand, item.model, item.serialNumber]
    .filter((v): v is string => !!v)
    .some((v) => norm(v).includes(q));
}

// ─── La photo et le QR code ───────────────────────────────────────────────

export const EQUIPMENT_PHOTO_BUCKET = "equipment-photos";
export const EQUIPMENT_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const EQUIPMENT_PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic"] as const;

/** Ce qui empêche d'envoyer cette photo, dit pour le patron ; nul si elle passe. */
export function equipmentPhotoProblem(file: { type: string; size: number }): string | null {
  if (file.size <= 0) return "Cette photo est vide.";
  if (file.size > EQUIPMENT_PHOTO_MAX_BYTES) {
    return `Cette photo dépasse 10 Mo (${(file.size / 1024 / 1024).toFixed(1).replace(".", ",")} Mo).`;
  }
  if (!(EQUIPMENT_PHOTO_MIME_TYPES as readonly string[]).includes(file.type)) {
    return "Envoyez une photo : JPEG, PNG, WebP ou HEIC.";
  }
  return null;
}

/**
 * `<entreprise>/<machine>/<id>.<ext>` : la base vérifie que la fiche pointe
 * son propre dossier, et la policy du bucket relit la machine par ce dossier.
 */
export function equipmentPhotoPath(tenantId: string, equipmentId: string, id: string, mimeType: string): string {
  const ext = mimeType === "image/png" ? "png" : mimeType === "image/webp" ? "webp" : mimeType === "image/heic" ? "heic" : "jpg";
  return `${tenantId}/${equipmentId}/${id}.${ext}`;
}

/**
 * L'adresse que porte le QR code collé sur la machine : sa fiche. Rien d'autre
 * — pas de jeton, pas de nom : celui qui scanne la lit avec ses propres droits.
 */
export function equipmentUrl(origin: string, equipmentId: string): string {
  return `${origin.replace(/\/+$/, "")}/materiel/${equipmentId}`;
}
